import { useQuery } from "@tanstack/react-query";
import { useRealtime } from "@tcg/realtime/react";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCan, usePos } from "@/app/context";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import { POS_TOPICS } from "@/lib/sse";
import { useRegisterTopic } from "@/realtime/useRegisterRealtime";
import type { SaleTaxFree } from "@/lib/types";

/**
 * 購入記録情報 of a 免税 sale (T07-30) on 完了 / the receipt screen: ⏳ 送信中 → ✅ 送信済み, or
 * ⚠ 事後送信 (tax-service could not take it yet — the minute job sends it; 「再送信」 now). Follows
 * the register's stream (`sale.changed` · `tax_free_record`).
 */
export function RecordStatus({ saleId, initial }: { saleId: string; initial: SaleTaxFree | null }) {
  const { t } = useTranslation();
  const { api } = usePos();
  const canRetry = useCan("tax.taxfree");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const record = useQuery({
    queryKey: ["pos-sales", "detail", saleId, "tax-free"],
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/orders/{sale_id}/tax-free", { params: { path: { sale_id: saleId } } }),
      ),
    ...(initial ? { initialData: initial } : {}),
  });
  // pos-service `sale.changed` of this sale, or the hub's `taxfree_record` of this register
  // (tax-service took the 購入記録情報 — T07-40)
  const registerTopic = useRegisterTopic();
  useRealtime(
    registerTopic ? [POS_TOPICS.sale, registerTopic] : [POS_TOPICS.sale],
    (message) => {
      if (message.id === saleId || message.entity === "taxfree_record") {
        void record.refetch();
      }
    },
    { onResync: () => void record.refetch() },
  );
  const data = record.data ?? initial;
  if (!data) {
    return null;
  }
  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await call(() =>
        api.POST("/v1/pos/orders/{sale_id}/tax-free/retry", {
          params: { path: { sale_id: saleId } },
          headers: { "X-Pos-Offline": "false" },
        }),
      );
      await record.refetch();
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  const label =
    data.status === "created"
      ? t("taxfree.record_sent")
      : data.status === "failed"
        ? t("taxfree.record_late")
        : t("taxfree.record_sending");
  return (
    <div
      data-testid="tf-status"
      data-status={data.status}
      className="flex flex-col gap-1 rounded-md border p-3 text-left"
    >
      <p className="flex items-center gap-2 text-lg">
        <span aria-hidden>
          {data.status === "created" ? "✅" : data.status === "failed" ? "⚠" : "⏳"}
        </span>
        {label}
      </p>
      {data.refundTrackingId ? (
        <p className="font-mono text-sm">{t("taxfree.tracking", { id: data.refundTrackingId })}</p>
      ) : null}
      {data.customsDeadline ? (
        <p className="text-sm">{t("taxfree.deadline", { date: data.customsDeadline })}</p>
      ) : null}
      {data.status !== "created" && canRetry ? (
        <button
          type="button"
          onClick={() => void retry()}
          disabled={busy}
          className="flex min-h-12 items-center justify-center gap-2 rounded-md border"
        >
          <RefreshCw aria-hidden className="size-4" />
          {t("taxfree.resend")}
        </button>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-pos-problem">
          {error}
        </p>
      ) : null}
    </div>
  );
}
